import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Copy, Edit3, History, MessageCircle, Plus, UserRound, X } from "lucide-react";
import { AdminOnlyBadge } from "./PaymentLinkFormModal";
import { fmtInr, fmtDate, linkUrl, waHref, isEditable, clientPrefill, copyUrl } from "./paymentLinkUtils";

const STATUS_CLS = {
    pending: "bg-amber-50 text-amber-800 border-amber-200", paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
    failed: "bg-rose-50 text-rose-800 border-rose-200", cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};
export const StatusChip = ({ status }) => (
    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold capitalize ${STATUS_CLS[status] || ""}`}>{status}</span>
);

const FIELD_LABELS = {
    amount: "Amount", session_count: "Sessions", description: "Description", student_name: "Student", parent_name: "Parent",
    enquiry_id: "Enquiry ID", client_search_name: "Client name", contact_number: "Contact", contact_country: "Country",
    payment_account_id: "Account", payment_account_label: "Account", status: "Status",
};
const show = (k, v) => (v === null || v === undefined || v === "" ? "—" : k === "amount" ? fmtInr(v) : k === "contact_number" ? `+${v}` : String(v));

/** Audit trail of a single link (created → each edit). */
export function LinkHistoryModal({ link, onClose }) {
    const entries = [...(link.edit_history || [])].reverse();
    return (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="payment-link-history-modal">
                <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
                    <h3 className="font-heading font-bold text-lg text-[#0F172A]">History · <span className="font-mono text-sm">{link.id}</span></h3>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Close"><X className="w-5 h-5" /></button>
                </div>
                <ol className="p-5 space-y-3 text-sm">
                    {entries.map((h, i) => (
                        <li key={`${h.at}-${i}`} className="border-l-2 border-[#1E3A8A] pl-3" data-testid={`payment-link-history-entry-${i}`}>
                            <div className="text-[11px] text-slate-500">{fmtDate(h.at)} · {h.by || "admin"}</div>
                            {Object.entries(h.changes || {}).filter(([k]) => k !== "payment_account_id").map(([k, [a, b]]) => (
                                <div key={k} className="text-slate-700"><span className="font-semibold">{FIELD_LABELS[k] || k}:</span> {show(k, a)} → <strong>{show(k, b)}</strong></div>
                            ))}
                        </li>
                    ))}
                    <li className="border-l-2 border-slate-300 pl-3">
                        <div className="text-[11px] text-slate-500">{fmtDate(link.created_at)} · {link.created_by || "admin"}</div>
                        <div className="text-slate-700">Created{link.parent_link_id ? <> as a follow-up to <span className="font-mono">{link.parent_link_id}</span></> : ""}</div>
                    </li>
                </ol>
            </div>
        </div>
    );
}

function ClientLinkRow({ it, onEdit, onHistory }) {
    const active = isEditable(it);
    return (
        <li className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid={`client-link-${it.id}`}>
            <span className="font-bold tabular-nums text-[#0F172A]">{fmtInr(it.amount)}</span>
            {it.session_count && <span className="text-xs text-slate-600">{it.session_count} sessions</span>}
            <StatusChip status={it.status} />
            <span className="text-[11px] text-slate-500 basis-full sm:basis-auto sm:flex-1 min-w-0 truncate">
                {it.description || "—"} · created {fmtDate(it.created_at)}{it.updated_at && it.updated_at !== it.created_at ? ` · edited ${fmtDate(it.updated_at)}` : ""}
            </span>
            <span className="flex items-center gap-1">
                {active && <>
                    <button onClick={() => copyUrl(linkUrl(it), toast)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Copy link" data-testid={`client-link-copy-${it.id}`}><Copy className="w-4 h-4" /></button>
                    <a href={waHref(it)} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" title="Reshare on WhatsApp" data-testid={`client-link-whatsapp-${it.id}`}><MessageCircle className="w-4 h-4" /></a>
                    <button onClick={() => onEdit(it)} className="p-1.5 rounded hover:bg-slate-100 text-[#1E3A8A]" title="Edit" data-testid={`client-link-edit-${it.id}`}><Edit3 className="w-4 h-4" /></button>
                </>}
                <button onClick={() => onHistory(it)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="History" data-testid={`client-link-history-${it.id}`}><History className="w-4 h-4" /></button>
            </span>
        </li>
    );
}

function ClientCard({ c, idx, onEdit, onHistory, onCreateFor }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`client-card-${idx}`}>
            <div className="flex flex-wrap items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] flex items-center justify-center shrink-0"><UserRound className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                    {c.client_search_name && (
                        <div className="flex flex-wrap items-center gap-2"><span className="font-bold text-[#0F172A]" data-testid={`client-card-search-name-${idx}`}>{c.client_search_name}</span><AdminOnlyBadge /></div>
                    )}
                    <div className="text-sm text-slate-700 break-words">
                        {[c.student_name && `Student: ${c.student_name}`, c.parent_name && `Parent: ${c.parent_name}`].filter(Boolean).join(" · ") || (!c.client_search_name && "Unnamed client")}
                    </div>
                    <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3">
                        {c.contact_number && <span data-testid={`client-card-phone-${idx}`}>+{c.contact_number}</span>}
                        {c.enquiry_id && <span>ID {c.enquiry_id}</span>}
                        <span>{c.links.length} request{c.links.length === 1 ? "" : "s"} · paid {fmtInr(c.total_paid)}{c.pending_count ? ` · ${c.pending_count} pending` : ""}</span>
                    </div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button onClick={() => setOpen((v) => !v)} data-testid={`client-card-history-${idx}`}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 hover:border-[#1E3A8A]">
                        <History className="w-3.5 h-3.5" /> View History <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    <button onClick={() => onCreateFor(clientPrefill(c))} data-testid={`client-card-new-link-${idx}`}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-[#1E3A8A] text-white text-xs font-semibold hover:bg-[#1E40AF]">
                        <Plus className="w-3.5 h-3.5" /> Create New Link
                    </button>
                </div>
            </div>
            {open && <ul className="mt-3 border-t border-slate-100 divide-y divide-slate-100">{c.links.map((it) => <ClientLinkRow key={it.id} it={it} onEdit={onEdit} onHistory={onHistory} />)}</ul>}
        </div>
    );
}

export function ClientResults({ clients, loading, q, onEdit, onHistory, onCreateFor }) {
    if (!q.trim()) return null;
    return (
        <section className="space-y-2" data-testid="payment-client-results">
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
                Clients matching “{q.trim()}” {loading ? "…" : `· ${clients.length}`}
            </div>
            {!loading && !clients.length && <div className="text-sm text-slate-500 bg-white border border-dashed border-slate-300 rounded-xl p-4" data-testid="payment-client-results-empty">No clients found.</div>}
            {clients.map((c, i) => <ClientCard key={c.key} c={c} idx={i} onEdit={onEdit} onHistory={onHistory} onCreateFor={onCreateFor} />)}
        </section>
    );
}
